import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  LinearProgress,
  Fade,
  Divider,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  GraphicEq as WaveformIcon,
  AccessTime as DurationIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { GlassButton } from "../../themes/UiElements";

// ─── Inline Audio Player ──────────────────────────────────────────────────────

const AudioPlayer = ({ url, durationReadable }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");

  const toggle = useCallback(
    (e) => {
      e.stopPropagation();
      if (!audioRef.current) return;
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    },
    [playing]
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      const pct = audio.duration
        ? (audio.currentTime / audio.duration) * 100
        : 0;
      setProgress(pct);
      const m = Math.floor(audio.currentTime / 60);
      const s = Math.floor(audio.currentTime % 60)
        .toString()
        .padStart(2, "0");
      setCurrentTime(`${m}:${s}`);
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime("0:00");
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: playing ? "primary.50" : "grey.50",
        border: "1px solid",
        borderColor: playing ? "primary.200" : "grey.200",
        borderRadius: 2,
        px: 1.2,
        py: 0.6,
        minWidth: 160,
        maxWidth: 200,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "primary.300",
          bgcolor: "primary.50",
        },
      }}
    >
      <audio ref={audioRef} src={url} preload="metadata" />

      <Tooltip title={playing ? "Pause" : "Play preview"}>
        <IconButton
          size="small"
          onClick={toggle}
          sx={{
            p: 0.5,
            bgcolor: playing ? "primary.main" : "primary.100",
            color: playing ? "white" : "primary.main",
            "&:hover": {
              bgcolor: playing ? "primary.dark" : "primary.200",
            },
            flexShrink: 0,
            transition: "all 0.15s ease",
          }}
        >
          {playing ? (
            <PauseIcon sx={{ fontSize: 16 }} />
          ) : (
            <PlayIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            borderRadius: 2,
            bgcolor: "grey.200",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              bgcolor: playing ? "primary.main" : "grey.400",
              transition: "none",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 0.3,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1 }}
          >
            {currentTime}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1 }}
          >
            {durationReadable}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Row Actions Dropdown ─────────────────────────────────────────────────────

const RowActionsMenu = ({
  file,
  onView,
  onApprove,
  onReject,
  onDeny,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const act = (fn) => () => {
    handleClose();
    fn(file);
  };

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 160,
            borderRadius: 2,
            "& .MuiMenuItem-root": {
              fontSize: 14,
              gap: 1.2,
              py: 0.9,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={act(onView)}>
          <VisibilityIcon fontSize="small" sx={{ color: "text.secondary" }} />
          View Details
        </MenuItem>

        {file.status === "pending" && [
          <Divider key="d1" />,
          <MenuItem key="approve" onClick={act(onApprove)}>
            <ApproveIcon fontSize="small" sx={{ color: "success.main" }} />
            <Typography sx={{ color: "success.main", fontWeight: 500 }}>
              Approve
            </Typography>
          </MenuItem>,
          <MenuItem key="deny" onClick={act(onDeny)}>
            <CancelIcon fontSize="small" sx={{ color: "error.main" }} />
            <Typography sx={{ color: "error.main", fontWeight: 500 }}>
              Reject
            </Typography>
          </MenuItem>,
        ]}

        {file.status === "approved" && [
          <Divider key="d2" />,
          <MenuItem key="reject" onClick={act(onReject)}>
            <CancelIcon fontSize="small" sx={{ color: "warning.main" }} />
            <Typography sx={{ color: "warning.main", fontWeight: 500 }}>
              Revoke Approval
            </Typography>
          </MenuItem>,
        ]}

        <Divider />
        <MenuItem onClick={act(onDelete)}>
          <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          <Typography sx={{ color: "error.main" }}>Delete</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────

const ConfirmModal = ({
  open,
  loading,
  onClose,
  onConfirm,
  title,
  description,
  file,
  confirmLabel,
  confirmColor = "error",
  confirmIcon,
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{description}</DialogContentText>
      {file && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>File:</strong> {file.fileName}
            </Typography>
            <Typography variant="body2">
              <strong>User ID:</strong> {file.userId}
            </Typography>
            <Typography variant="body2">
              <strong>Category:</strong> {file.promptCategory}
            </Typography>
          </Stack>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <Button onClick={onClose} disabled={loading} color="inherit">
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={loading}
        variant="contained"
        color={confirmColor}
        startIcon={
          loading ? <CircularProgress size={18} color="inherit" /> : confirmIcon
        }
      >
        {loading ? `${confirmLabel}ing...` : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AllVoiceFilesList = () => {
  const navigate = useNavigate();

  const [voiceFiles, setVoiceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const modalBase = { open: false, file: null, loading: false };
  const [approveModal, setApproveModal] = useState(modalBase);
  const [rejectModal, setRejectModal] = useState(modalBase);
  const [denyModal, setDenyModal] = useState(modalBase);
  const [deleteModal, setDeleteModal] = useState(modalBase);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchVoiceFiles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_ENDPOINT}/root/voice-files`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch,
          status: statusFilter,
          sortBy: "createdAt",
          sortOrder: "desc",
        },
        ...getHeader(),
      });
      setVoiceFiles(response.data.data);
      setTotalCount(response.data.pagination.totalCount);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch voice files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoiceFiles();
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  // ── action helpers ──

  const patchFile = async (id, action, setModal) => {
    setModal((p) => ({ ...p, loading: true }));
    try {
      await axios.patch(
        `${API_ENDPOINT}/root/voice-files/${id}/${action}`,
        {},
        getHeader()
      );
      setModal(modalBase);
      fetchVoiceFiles();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${action} voice file`);
      setModal((p) => ({ ...p, loading: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteModal((p) => ({ ...p, loading: true }));
    try {
      await axios.delete(
        `${API_ENDPOINT}/voice/voice-files/${deleteModal.file._id}`,
        getHeader()
      );
      setDeleteModal(modalBase);
      fetchVoiceFiles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete voice file");
      setDeleteModal((p) => ({ ...p, loading: false }));
    }
  };

  const getStatusColor = (status) => {
    const map = {
      completed: "success",
      approved: "success",
      uploading: "warning",
      failed: "error",
      pending: "info",
      rejected: "error",
    };
    return map[status] ?? "default";
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Voice Files
          </Typography>
          <GlassButton
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => navigate("/home/upload-files")}
          >
            Upload New File
          </GlassButton>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search by filename, user ID, category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="uploading">Uploading</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchVoiceFiles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>File ID</strong>
                </TableCell>
                <TableCell>
                  <strong>File Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Category</strong>
                </TableCell>
                <TableCell>
                  <strong>Size / Duration</strong>
                </TableCell>
                <TableCell>
                  <strong>Preview</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Uploaded</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : voiceFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {debouncedSearch || statusFilter
                        ? "No voice files found matching your criteria"
                        : "No voice files uploaded yet"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                voiceFiles.map((file) => (
                  <TableRow key={file._id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: 12 }}
                      >
                        {file?.voiceFileId ?? "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={file.originalFileName}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.fileName}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={file.promptCategory}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Size + Duration */}
                    <TableCell>
                      <Stack spacing={0.3}>
                        <Typography variant="body2">
                          {file.fileSizeReadable}
                        </Typography>
                        {file.durationReadable && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              color: "text.secondary",
                            }}
                          >
                            <DurationIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption">
                              {file.durationReadable}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Audio Preview */}
                    <TableCell>
                      {file.voiceFileUrl ? (
                        <AudioPlayer
                          url={file.voiceFileUrl}
                          durationReadable={file.durationReadable ?? ""}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={file.status}
                        size="small"
                        color={getStatusColor(file.status)}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(file.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {file.uploadedBy}
                      </Typography>
                    </TableCell>

                    {/* Actions dropdown */}
                    <TableCell align="center">
                      <RowActionsMenu
                        file={file}
                        onView={(f) => navigate(`/home/${f._id}/voice-files`)}
                        onApprove={(f) =>
                          setApproveModal({
                            open: true,
                            file: f,
                            loading: false,
                          })
                        }
                        onReject={(f) =>
                          setRejectModal({
                            open: true,
                            file: f,
                            loading: false,
                          })
                        }
                        onDeny={(f) =>
                          setDenyModal({ open: true, file: f, loading: false })
                        }
                        onDelete={(f) =>
                          setDeleteModal({
                            open: true,
                            file: f,
                            loading: false,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* ── Modals ── */}

      <ConfirmModal
        open={approveModal.open}
        loading={approveModal.loading}
        onClose={() => setApproveModal(modalBase)}
        onConfirm={() =>
          patchFile(approveModal.file._id, "approve", setApproveModal)
        }
        title="Approve Voice File"
        description="Are you sure you want to approve this voice file?"
        file={approveModal.file}
        confirmLabel="Approve"
        confirmColor="success"
        confirmIcon={<ApproveIcon />}
      />

      <ConfirmModal
        open={rejectModal.open}
        loading={rejectModal.loading}
        onClose={() => setRejectModal(modalBase)}
        onConfirm={() =>
          patchFile(rejectModal.file._id, "reject", setRejectModal)
        }
        title="Revoke Approval"
        description="This will move the file back to pending status."
        file={rejectModal.file}
        confirmLabel="Revoke"
        confirmColor="warning"
        confirmIcon={<CancelIcon />}
      />

      <ConfirmModal
        open={denyModal.open}
        loading={denyModal.loading}
        onClose={() => setDenyModal(modalBase)}
        onConfirm={() => patchFile(denyModal.file._id, "deny", setDenyModal)}
        title="Reject Voice File"
        description="This will permanently reject the file."
        file={denyModal.file}
        confirmLabel="Reject"
        confirmColor="error"
        confirmIcon={<CancelIcon />}
      />

      <ConfirmModal
        open={deleteModal.open}
        loading={deleteModal.loading}
        onClose={() => setDeleteModal(modalBase)}
        onConfirm={handleDeleteConfirm}
        title="Delete Voice File"
        description="This action cannot be undone. The file will be permanently deleted."
        file={deleteModal.file}
        confirmLabel="Delete"
        confirmColor="error"
        confirmIcon={<DeleteIcon />}
      />
    </Box>
  );
};

export default AllVoiceFilesList;
